import { transformOrderToDocument } from '~/utils/printing/transformers'
import { THERMAL_WIDTH } from '~/utils/printing/types'
import type { PrintRequest, PrintResponse } from '~/types/print'

const transformOrderToPrintFormat = (orderResponse: any, t: (key: string) => string): PrintRequest => {
  console.log('🔍 DEBUG: orderResponse structure:', orderResponse)

  // Handle both direct order format and wrapped order format
  const order = orderResponse.order || orderResponse

  if (!order) {
    throw new Error('Order data is missing')
  }

  console.log('🔍 DEBUG: order structure:', order)

  // Get business information from admin cookie
  const adminCookie = useCookie('admin')
  const adminData = adminCookie.value as any || null
  const businessInfo = {
    name: adminData?.business?.name || 'tapy.',
    address: adminData?.business?.address || undefined,
    phone: adminData?.business?.phone || undefined,
  }

  // Use the new standardized transformation to generate document
  const document = transformOrderToDocument(order, businessInfo, THERMAL_WIDTH.SMALL, t)
  // Send the complete receipt as raw lines
  // The Atreus print server needs to be updated to handle this format

  console.log('📄 Generated document:', document)

  return {
    lines: document.lines,
  }
}

const getPrintServerUrl = (): string => {
  if (import.meta.client) {
    const { value } = usePrintServerUrl()
    return value
  }
  return 'https://192.168.0.120:3031'
}

const printToServer = async (orderData: any, t: (key: string) => string): Promise<void> => {
  const log = useLogger('composable:useLocalPrintServer')
  try {
    if (!orderData) {
      throw new Error('Order data is required')
    }

    const printServerUrl = getPrintServerUrl().replace(/\/$/, '') // Remove trailing slash

    // Transform order data to print server format
    const printData = transformOrderToPrintFormat(orderData, t)

    console.log('Final payload to print server:', printData)

    // Test health endpoint first
    console.log('Testing print server health...')
    try {
      const healthCheck = await $fetch(`${printServerUrl}/health`)
      console.log('Health check response:', healthCheck)
    }
    catch (healthError) {
      console.log('Health check failed:', healthError)
    }

    // Send print request to print server

    const response = await $fetch<PrintResponse>(`${printServerUrl}/print`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printData),
    })

    console.log('Print server response:', response)

    if (response.success) {
      useSuccessToast(response.message || 'Receipt printed successfully')
    }
    else {
      throw new Error(response.message || 'Print failed')
    }
  }
  catch (error) {
    log.error('Failed to print via print server:', error)
    throw error // Re-throw to let the wrapper handle the fallback
  }
}

export const useLocalPrintServer = () => {
  const { t } = useI18n()

  return {
    printToServer: (orderData: any) => printToServer(orderData, t),
    transformOrderToPrintFormat: (orderData: any) => transformOrderToPrintFormat(orderData, t),
  }
}
